<script setup>
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';

import { api } from '@/api/client.js';
import Badge from '@/components/ds/Badge.vue';
import BarRankRow from '@/components/panels/BarRankRow.vue';
import ListPager from '@/components/panels/ListPager.vue';
import PanelCard from '@/components/panels/PanelCard.vue';
import { useListPage } from '@/composables/useListPage.js';
import { usePanelState } from '@/composables/usePanelState.js';
import { usePoll } from '@/composables/usePoll.js';
import { useTenantStore } from '@/stores/tenant.js';
import { REFERRERS_EMPTY } from '@/utils/emptyStateCopy.js';

const tenant = useTenantStore();
const { activeAppId, timeWindow } = storeToRefs(tenant);
const ps = usePanelState('loading');
const data = ref({ total: 0, rows: [] });

async function fetchFn({ signal }) {
  if (!activeAppId.value) return;
  return api.events.referrers({ appId: activeAppId.value, window: timeWindow.value, signal });
}

const { tick, status: refreshStatus, lastUpdatedAt } = usePoll(fetchFn, {
  watch: [() => tenant.changeKey],
  onSuccess(result) {
    if (!result || result.ok !== true) return;
    data.value = { total: result.total ?? 0, rows: result.rows ?? [] };
    ps.setState(data.value.rows.length === 0 ? 'empty' : 'data');
  },
  onError(err) {
    ps.setState('error', {
      message: "Couldn't load traffic sources",
      detail: err?.message ?? '',
    });
  },
});

ps.setRetryHandler(() => tick());

// Exponer ps para los panelRefs del LandingView (error global / retry all).
defineExpose({ ps, refreshStatus });

const CATEGORY_TONE = Object.freeze({
  search: 'info',
  social: 'accent',
  direct: 'neutral',
  other: 'neutral',
});

const rows = computed(() => data.value.rows.map((row) => ({
  ...row,
  pct: data.value.total > 0 ? (row.count / data.value.total) * 100 : null,
  tone: CATEGORY_TONE[row.category] ?? 'neutral',
})));
const { page, visible } = useListPage(rows, 5);
watch(() => tenant.changeKey, () => { page.value = 1; });
</script>

<template>
  <PanelCard
    title="Traffic sources"
    help-key="trafficSources"
    :state="ps.state.value"
    :empty-headline="REFERRERS_EMPTY.headline"
    :empty-body="REFERRERS_EMPTY.body"
    :error-message="ps.errorMessage.value"
    :error-detail="ps.errorDetail.value"
    :on-retry="ps.retry"
    :refresh-status="refreshStatus"
    :last-updated-at="lastUpdatedAt"
  >
    <div class="referrers">
      <p class="referrers__summary">
        <strong>{{ data.total.toLocaleString() }}</strong> pageviews by acquisition source
      </p>
      <div class="referrers__labels">
        <span>Source</span><span>Views · share of all pageviews</span>
      </div>
      <BarRankRow
        v-for="row in visible"
        :key="row.source"
        :label="row.source"
        :count="row.count"
        :pct="row.pct"
      >
        <Badge
          v-if="row.category !== 'other'"
          :tone="row.tone"
        >
          {{ row.category }}
        </Badge>
      </BarRankRow>
      <ListPager
        v-model:page="page"
        :total="rows.length"
        :page-size="5"
        label="Traffic sources pagination"
      />
    </div>
    <template
      v-if="rows.length"
      #tableFallback
    >
      <table class="rf-table">
        <caption class="visually-hidden">
          Traffic sources by pageviews in this window
        </caption>
        <thead>
          <tr>
            <th scope="col">
              Source
            </th>
            <th scope="col">
              Category
            </th>
            <th
              scope="col"
              class="numeric"
            >
              Pageviews
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="`t-${row.source}`"
          >
            <td>{{ row.source }}</td>
            <td>{{ row.category }}</td>
            <td class="numeric">
              {{ row.count }}
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </PanelCard>
</template>

<style scoped>
.referrers {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.referrers__summary {
  margin: 0 0 var(--space-2);
  color: var(--fg-secondary);
  font-size: var(--text-sm);
}

.referrers__summary strong {
  color: var(--fg-primary);
  font-size: var(--text-2xl);
  font-variant-numeric: tabular-nums;
}

.referrers__labels {
  display: flex;
  justify-content: space-between;
  gap: var(--space-3);
  font-size: var(--text-xs);
  color: var(--fg-secondary);
}

.rf-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-xs);
}

.rf-table th,
.rf-table td {
  padding: var(--space-1) var(--space-2);
  text-align: left;
  border-bottom: var(--bw-thin) solid var(--border-default);
}

.rf-table th {
  color: var(--fg-secondary);
  font-weight: var(--fw-medium);
}

.rf-table .numeric {
  text-align: right;
  font-feature-settings: "tnum";
}
</style>
